import { Directive, Input, TemplateRef, inject } from '@angular/core';
import type { Observable } from 'rxjs';
import type { BuilderLayoutConfig, CapabilityRegistryEntry, FormatControl, FormulaBuilderConfig, LabelValueOption } from '../shared/self-service-config.types';
import type { DashboardTile, InputApiCallConfig, TargetFormulaReferenceEntry } from './dvm-layout-builder.types';
import type { DrillDownStepComponent } from '../components/drill-down-step/drill-down-step.component';

/**
 * Contract between the DVM layout builder SHELL (`DvmLayoutBuilderComponent`)
 * and its per-widget STEP components (`self-service-visualization/components/*`).
 *
 * The shell provides itself under this token
 * (`{ provide: DvmBuilderHost, useExisting: forwardRef(() => DvmLayoutBuilderComponent) }`)
 * and every step injects it. A step may ONLY reach the shell through the
 * members declared here — anything else it needs is either its own state or
 * a shared service. Keeping the surface explicit is what lets each step be
 * compiled, read and changed on its own.
 *
 * Members are grouped by concern; each is documented on the shell.
 */
export abstract class DvmBuilderHost {
  /** Optional Design Builder dropdown behaviour; other builders keep their existing form. */
  readonly supportsDropdownFirstValue?: boolean;
  readonly firstValueDropdownOptions?: LabelValueOption[];
  /** Design Builder may route a family's compute request through its object API. */
  requestGenerate?: (url: string, payload: Record<string, unknown>) => Observable<Record<string, unknown>>;
  // ── layout / config ─────────────────────────────────────────────────────
  abstract layout: BuilderLayoutConfig;
  abstract selectedLayoutType: string;
  /** Table & Matrix sub-type ('normal' | 'editable' | 'drilldown' | legacy) — owned by the table-matrix step. */
  abstract get tmType(): string;
  abstract get isTmDrilldown(): boolean;
  /** The drill-down step — the table-matrix step composes it for its drill-down sub-type. */
  abstract get drillDownStep(): DrillDownStepComponent;
  /**
   * Pick a Table type. `nested` is built by its own form (layout type
   * NESTED_TABLE); every other type is a Table & Matrix sub-type.
   */
  abstract switchTableType(type: string): void;
  abstract get activeCapabilities(): CapabilityRegistryEntry;
  abstract chartSupports(capability: string): boolean;
  abstract chartExposesSection(section: string): boolean;
  abstract layoutSectionTitle(sectionId: string, fallback: string): string;
  abstract layoutSectionHint(sectionId: string, fallback?: string): string;
  abstract fieldEnabled(sectionId: string, fieldKey: string): boolean;
  abstract fieldLabel(sectionId: string, fieldKey: string, fallback: string): string;
  abstract get fontWeightOptions(): LabelValueOption[];
  abstract get measureAggregations(): LabelValueOption[];
  abstract get numberFormats(): LabelValueOption[];
  abstract get formatControls(): FormatControl[];
  abstract get formulaBuilderConfig(): FormulaBuilderConfig;
  abstract applicableFormatControls(layoutType: string): FormatControl[];
  abstract formatControlOptions(control: FormatControl, tile?: DashboardTile): LabelValueOption[];
  abstract formatControlVisible(control: FormatControl, format: Record<string, unknown> | undefined): boolean;
  abstract defaultTileFormat(layoutType: string): Record<string, unknown>;
  /** Accordion group order (config `formatGroupOrder`, else the built-in Title/Axis/Tooltip/… order). */
  abstract get formatGroupOrder(): string[];
  /** Colours of a named palette from config `options.palettes`. */
  abstract resolvePaletteColors(paletteKey?: string): string[] | undefined;

  // ── the chart being built / edited ──────────────────────────────────────
  abstract title: string;
  abstract componentId: string;
  abstract dashboardId: number | null;
  abstract componentType: string;
  abstract editingTileId: string | null;
  abstract previewTileId: string | null;
  abstract generateError: string;
  abstract isGenerating: boolean;

  // ── datasets / columns (Data pane) ──────────────────────────────────────
  abstract selectedDatasets: string[];
  abstract entityOptions: string[];
  abstract entitiesLoading: boolean;
  abstract get drillDataset(): string;
  abstract get drillDatasetColumns(): string[];
  abstract get isDrillDatasetColumnsLoading(): boolean;
  abstract get drillColumnsErrorMessage(): string;
  abstract onSelectedDatasetsChange(datasets: string[] | null): void;
  abstract loadColumnsForDataset(dataset: string): void;
  abstract columnsForRow(rowId: string, dataset: string): string[];
  /** Optional: every column incl. uuid / system attributes (not filtered by `hiddenColumns`). */
  allColumnsForRow?: (rowId: string, dataset: string) => string[];
  abstract forgetRowColumns(rowId: string): void;
  abstract isDatasetColumnsLoading(dataset: string): boolean;
  abstract cachedColumns(dataset: string): string[] | undefined;
  abstract cachedRows(dataset: string): Array<Record<string, unknown>> | undefined;
  abstract drillColumnPlaceholder(kind: 'dimension' | 'measure'): string;
  /** Column pick list of the build form: the dataset's columns plus, on the Bar form, its computed columns. */
  abstract get builderColumns(): string[];
  abstract columnPlaceholder(): string;

  // ── drag & drop of a Data-pane field onto a form control ────────────────
  abstract allowFieldDrop(event: DragEvent): void;
  abstract readFieldDrop(event: DragEvent): { dataset: string; field: string } | null;
  abstract useDroppedSingleDataset(dataset: string): void;
  abstract appendFormulaToken(current: string, field: string): string;

  // ── shared Format accordion state ───────────────────────────────────────
  abstract barFormat: Record<string, unknown>;
  /** A control of the shared Format accordion changed (title sync + live tile refresh). */
  abstract onBarFormatChange(): void;

  // ── chart targets / formula references (shared by inputs and charts) ────
  abstract selectedTargetCharts: string[];
  abstract interactionsSectionOpen: boolean;
  abstract buildApiCallConfig: InputApiCallConfig;
  abstract get targetCharts(): LabelValueOption[];
  abstract targetLabelForId(id: string): string;
  abstract targetLayoutTypeForId(id: string): string;
  abstract isInputControlTargetId(targetId: string): boolean;
  abstract isWaterfallTarget(targetId: string): boolean;
  abstract targetSteps(targetId: string): Array<{ label: string; stepType: string }>;
  abstract trackByStepLabel(index: number, step: { label: string; stepType: string }): string;
  /** Per-target formulas of an input control (flat targets). */
  abstract targetFormulas: Record<string, string>;
  /** Per-target, per-bar formulas of an input control (WATERFALL targets). */
  abstract targetStepFormulas: Record<string, Record<string, string>>;
  /** Typed references behind the [Token]s of every formula, keyed by target / own componentId. */
  abstract targetFormulaReferences: Record<string, TargetFormulaReferenceEntry[]>;
  abstract targetStepFormula(targetId: string, stepLabel: string): string;
  abstract setTargetStepFormula(targetId: string, stepLabel: string, value: string): void;
  abstract targetFormulaSummary(targetId: string): string;
  abstract targetFormulasForPayload(
    targetId: string,
  ): Array<{ step?: string; formula: string; references?: Record<string, { inputTitle: string; targetLabel: string; rowLabel: string }> }>;
  abstract onTargetFormulaDrop(event: DragEvent, targetId: string, stepLabel?: string): void;
  /** Saved / published dashboards a popup button or table cell can navigate to. */
  abstract get tmNavTargetDashboards(): LabelValueOption[];
  /** Keep a restored navigation target in the list even when it is no longer among the saved dashboards. */
  abstract ensureNavTargetInList(slug: string): void;
  /** Widgets of a target dashboard (by slug) a table cell can navigate to. */
  abstract navTargetComponentOptions(slug: string): LabelValueOption[];
  abstract targetReferences(targetId: string, step?: string): TargetFormulaReferenceEntry[];
  abstract referencesForFormula(
    targetId: string,
    formula: string,
    step?: string,
  ): Record<string, { inputTitle: string; targetLabel: string; rowLabel: string }> | undefined;
  abstract rehydrateOwnFormulaReferences(componentId: string, references: unknown): void;
  abstract formulaReferencesEnabledFor(layoutType: string): boolean;
  abstract chartTargetsPayload(): Record<string, unknown>;
  abstract headerFormatPayload(): Record<string, unknown>;
  abstract apiCallConfigPayload(): Record<string, unknown>;

  // ── dashboard tiles (preview canvas) ────────────────────────────────────
  abstract dashboardTiles: DashboardTile[];
  abstract buildPayload(): Record<string, unknown>;
  abstract newTileBase(payload: Record<string, unknown>): DashboardTile;
  abstract addTile(tile: DashboardTile): boolean;
  /** Default Generate flow's tile mapping (response → tile of the selected layout type) → canvas. */
  abstract addTileFromResponse(response: Record<string, unknown> | null, payload: Record<string, unknown>): boolean;
  abstract updateTileInPlace(next: DashboardTile): void;
  abstract findTileAnywhere(id: string): DashboardTile | undefined;
  abstract removePreviewTile(): void;
  abstract invalidatePreview(): void;
  /** Design Builder: show a nested-table column setting (width, currency…) on the canvas at once (no regenerate). */
  setNestedColumnOverride?(levelIndex: number, field: string, patch: Record<string, unknown>): void;
  abstract onTileFormatChange(tile: DashboardTile): void;
  abstract onTileDropdownChange(tile: DashboardTile, value: string | string[], basePayload?: Record<string, unknown>): void;
  /** Design Builder: reload just this dropdown's own option list (after its settings were regenerated). */
  reloadDropdownOptions?(tile: DashboardTile): void;
  abstract reflowCharts(): void;
  /** True when `rows` carry the field a client-rendered chart's capabilities expect (empty rows always fit). */
  abstract rowsFitClientChart(rows: Array<Record<string, unknown>>, data: Record<string, unknown>, fieldKey: 'category' | 'xField' | 'categoryField'): boolean;
}

/**
 * What the shell asks of every step. The shell owns the SHARED part of a
 * chart (title, componentId, datasets, targets, format accordion) and runs
 * the hubs (Generate, Edit, New); each step owns its family's fields.
 */
/** A recompute / API response for an existing tile, as handed to `DvmBuilderStep.applyRecomputeResult`. */
export interface TileRecomputeResult {
  /** The raw response entry (`{ componentId, result }` or the result itself). */
  entry: Record<string, unknown>;
  /** `entry.result ?? entry`. */
  result: Record<string, unknown>;
  /** The tile's Format, falling back to the layout's default Format. */
  format: Record<string, unknown>;
}

export interface DvmBuilderStep {
  /** Family-specific "Still needed" items — include the dataset check when the family needs one. */
  blockers(): string[];
  /** Family-specific payload posted to the generate API (the shell adds `bde_relation`). */
  buildPayload(): Record<string, unknown>;
  /** Re-populate the form from an existing tile (Edit). The shell has already restored title / componentId / datasets / targets. */
  loadFromTile(tile: DashboardTile, payload: Record<string, unknown>): void;
  /** Clear the family's fields for a brand-new chart (the shell resets shared state). */
  reset(): void;
  /** Layout type switched to this family (or Edit / New re-activated it): seed defaults. */
  onLayoutActivated?(): void;
  /** The shared Format accordion (`host.barFormat`) changed. */
  onFormatChanged?(): void;
  /** Rows of `dataset` finished loading (client-rendered charts refresh their preview). */
  onDatasetRowsLoaded?(dataset: string): void;
  /** The selected dataset list changed (rows pointing at a dropped dataset should be re-based). */
  onDatasetsChanged?(): void;
  /** The PRIMARY dataset changed: drop fields that the new dataset does not have (once its columns are known). */
  onDatasetChanged?(dataset: string, previous: string): void;
  /** Columns of `dataset` finished loading. */
  onDatasetColumnsLoaded?(dataset: string): void;
  /** A validation message that blocks Generate (shown as the generate error), or '' when fine. */
  preGenerateError?(): string;
  /**
   * Run the Generate flow for this family. Return `false` to let the shell
   * run the default flow (POST payload → `addTileFromResponse`).
   */
  generate?(payload: Record<string, unknown>, url: string): boolean;
  /**
   * Default Generate flow: build the tile from the generate API response.
   * `base` already carries id / componentId / layoutType / title / payload and
   * the merged Format (layout default + the shared Format accordion). A family
   * without this hook renders the response as a waterfall (legacy default).
   */
  tileFromResponse?(base: DashboardTile, response: Record<string, unknown> | null, payload: Record<string, unknown>): DashboardTile;
  /** A tile's own Format popup changed: re-derive the widget data from `format` (the shell then re-renders). */
  applyFormatToTile?(tile: DashboardTile, format: Record<string, unknown>): void;
  /** Apply a recompute result to an existing tile; `false` leaves the tile untouched. */
  applyRecomputeResult?(tile: DashboardTile, recompute: TileRecomputeResult): boolean;
}

/**
 * Base class of every step component: injects the host, exposes `active`
 * (the step's template gates itself on it) and receives the shell's shared
 * ng-templates (dataset picker, reference list, format accordion, API-call
 * block) so the markup is defined once, in the shell.
 */
@Directive()
export abstract class DvmBuilderStepBase implements DvmBuilderStep {
  protected readonly host = inject(DvmBuilderHost);

  /** Layout types this step builds. */
  abstract readonly layoutTypes: readonly string[];

  @Input() datasetPicker: TemplateRef<unknown> | null = null;
  @Input() referenceList: TemplateRef<unknown> | null = null;
  @Input() chartFormatAccordion: TemplateRef<unknown> | null = null;
  @Input() apiCallConfigBlock: TemplateRef<unknown> | null = null;

  get active(): boolean {
    return this.layoutTypes.includes(this.host.selectedLayoutType);
  }

  abstract blockers(): string[];
  abstract buildPayload(): Record<string, unknown>;
  abstract loadFromTile(tile: DashboardTile, payload: Record<string, unknown>): void;
  abstract reset(): void;
}
